import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import type {
  AttendanceEvent,
  AttendanceEventFilters,
  AttendanceEventsSummary,
  PaginatedAttendanceEvents,
  CreateAttendanceEventDto,
} from '../types/attendance.types'

const ATTENDANCE_QUERY_KEY = 'attendance-events'

/** Сырой ответ GET /attendance-events: пагинация в `meta`, сводка в `summary` */
interface AttendanceEventsResponse {
  data?: AttendanceEvent[]
  meta?: { page?: number; limit?: number; total?: number; totalPages?: number }
  summary?: AttendanceEventsSummary
}

/**
 * <input type="date"> отдаёт 'YYYY-MM-DD'. Бэкенд делает `new Date(value)` —
 * это полночь UTC, и `dateTo` отрезал бы весь выбранный день. Переводим в
 * границы суток по локальному времени браузера.
 */
const toDayBoundary = (value: string | undefined, end: boolean): string | undefined => {
  if (!value) return undefined
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const d = new Date(`${value}T00:00:00`)
  if (end) d.setHours(23, 59, 59, 999)
  return d.toISOString()
}

export const useAttendanceEvents = (filters: AttendanceEventFilters = {}) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedAttendanceEvents> => {
      const response = (await apiClient.getAttendanceEvents({
        ...filters,
        dateFrom: toDayBoundary(filters.dateFrom, false),
        dateTo: toDayBoundary(filters.dateTo, true),
      })) as unknown as AttendanceEventsResponse
      // Бэкенд отдаёт пагинацию в `meta`, а не на верхнем уровне
      const meta = response.meta ?? {}
      return {
        data: response.data || [],
        total: meta.total || 0,
        page: meta.page || 1,
        limit: meta.limit || filters.limit || 20,
        totalPages: meta.totalPages || 1,
        summary: response.summary ?? null,
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  })
}

export const useAttendanceEvent = (id: string) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, id],
    queryFn: async (): Promise<AttendanceEvent> => {
      const response = await apiClient.get(`/api/v1/attendance-events/${id}`)
      return response.data
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateAttendanceEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateAttendanceEventDto) => {
      const response = await apiClient.createAttendanceEvent(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_QUERY_KEY] })
    },
  })
}

export const useAttendanceStatistics = (filters: AttendanceEventFilters = {}) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, 'statistics', filters],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/attendance-events/statistics', {
        params: filters,
      })
      return response.data
    },
    staleTime: 60 * 1000, // 1 minute
  })
}

export const useAttendanceEventsByEmployee = (employeeId: string, filters: AttendanceEventFilters = {}) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, 'employee', employeeId, filters],
    queryFn: async (): Promise<PaginatedAttendanceEvents> => {
      const response = await apiClient.get('/api/v1/attendance-events/by-employee', {
        params: { employeeId, ...filters },
      })
      const meta = response.meta ?? response
      return {
        data: response.data || [],
        total: meta.total || 0,
        page: meta.page || 1,
        limit: meta.limit || 20,
        totalPages: meta.totalPages || 1,
        summary: null,
      }
    },
    enabled: !!employeeId,
    staleTime: 30 * 1000,
  })
}